import { argon2id } from 'hash-wasm';
import { base64ToBytes, bytesToBase64 } from './utils';

interface KdfParams {
  iterations: number;
  memory: number;
  parallelism: number;
}

interface DerivedKeys {
  authKeyHash: string;
  encryptionKey: Uint8Array;
  verifier: string;
}

export async function deriveKeys(
  password: string,
  salt: string,
  params: KdfParams
): Promise<DerivedKeys> {
  const saltBytes = base64ToBytes(salt);

  // Derive 64 bytes (2 keys of 32 bytes each)
  const hashHex = await argon2id({
    password,
    salt: saltBytes,
    iterations: params.iterations,
    memorySize: params.memory,
    parallelism: params.parallelism,
    hashLength: 64,
    outputType: 'hex',
  });

  // Convert hex to bytes
  const masterKey = hexToBytes(hashHex);

  // Split into 2 keys
  const authKey = masterKey.slice(0, 32);
  const encryptionKey = masterKey.slice(32, 64);

  // Hash the authKey with SHA-256 before sending to server
  const authKeyHash = await hashForServer(authKey);

  // Create verifier for local validation
  const verifier = await createVerifier(encryptionKey);

  return {
    authKeyHash,
    encryptionKey: new Uint8Array(encryptionKey),
    verifier,
  };
}

function hexToBytes(hex: string): Uint8Array {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.substring(i, i + 2), 16);
  }
  return bytes;
}

async function hashForServer(key: Uint8Array): Promise<string> {
  const hashBuffer = await crypto.subtle.digest('SHA-256', key.buffer as ArrayBuffer);
  return bytesToBase64(new Uint8Array(hashBuffer));
}

async function createVerifier(key: Uint8Array): Promise<string> {
  const data = new TextEncoder().encode('oneckpass-verifier');
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    key.buffer as ArrayBuffer,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );
  const signature = await crypto.subtle.sign('HMAC', cryptoKey, data);
  return bytesToBase64(new Uint8Array(signature));
}
